import type {ContractChild, ContractParent} from "./contractCoverageData";
import {COVERAGE_CURRENCY} from "./contractCoverageData";
import {formatCompactCurrency, formatDate, formatNumber, POSITION_LABEL} from "./contractCoverageUtils";

/**
 * Execution planning over a user-chosen subset of a parent's child suppliers.
 *
 * The user selects several children (e.g. the three German entities of a group),
 * states an intention, and the system generates a scoped plan: an aggregated
 * scope summary plus suggested next steps derived from the children's contract
 * positions and the chosen intention. Further inputs will be layered on later.
 */

export type PlanIntention = 'consolidate' | 'renegotiate' | 'rationalise' | 'reevaluate';

export const PLAN_INTENTIONS: {value: PlanIntention; label: string; description: string}[] = [
    {
        value: 'consolidate',
        label: 'Consolidate under one agreement',
        description: 'Bring the selected suppliers under a single master agreement or contract.',
    },
    {
        value: 'renegotiate',
        label: 'Renegotiate commercial terms',
        description: 'Re-open pricing and scope on the selected suppliers, jointly.',
    },
    {
        value: 'rationalise',
        label: 'Rationalise & reduce suppliers',
        description: 'Reduce duplication and concentrate volume on fewer suppliers.',
    },
    {
        value: 'reevaluate',
        label: 'Re-evaluate before renewal',
        description: 'Review fit and right-size ahead of contract expiry.',
    },
];

export type PlanScope = {
    count: number;
    spend: number;
    saving: number;
    avoidance: number;
    transactions: number;
    entities: number;
    countries: string[];
    contracted: number;
    uncontracted: number;
    uncontractedSpend: number;
    overSpend: number;
    aboveCount: number;
    belowCount: number;
    requiredRecommended: number;
    earliestExpiration: string | null;
    positionMix: {position: ContractChild['position']; count: number}[];
};

export type ExecutionPlan = {
    intention: PlanIntention;
    intentionLabel: string;
    scope: PlanScope;
    steps: string[];
};

function buildScope(children: ContractChild[]): PlanScope {
    const countries = Array.from(new Set(children.map(c => c.country).filter(Boolean) as string[]));
    const above = children.filter(c => c.position === 'above_contracted_value');
    const below = children.filter(c => c.position === 'below_expected_utilization');
    const uncontractedKids = children.filter(c => c.contractCount === 0);
    const reqRec = children.filter(c =>
        c.position === 'contract_required' || c.position === 'contract_recommended');

    const expirations = children
        .map(c => c.nextExpiration)
        .filter(Boolean)
        .sort() as string[];

    const mixMap = new Map<ContractChild['position'], number>();
    for (const c of children) mixMap.set(c.position, (mixMap.get(c.position) ?? 0) + 1);

    return {
        count: children.length,
        spend: children.reduce((s, c) => s + c.spend, 0),
        saving: children.reduce((s, c) => s + c.costSavingEur, 0),
        avoidance: children.reduce((s, c) => s + c.costAvoidanceEur, 0),
        transactions: children.reduce((s, c) => s + c.transactions, 0),
        entities: children.reduce((s, c) => s + c.entities, 0),
        countries,
        contracted: children.filter(c => c.contractCount > 0).length,
        uncontracted: uncontractedKids.length,
        uncontractedSpend: uncontractedKids.reduce((s, c) => s + c.spend, 0),
        overSpend: above.reduce((s, c) => s + Math.max(0, c.spend - c.contractedValueEur), 0),
        aboveCount: above.length,
        belowCount: below.length,
        requiredRecommended: reqRec.length,
        earliestExpiration: expirations[0] ?? null,
        positionMix: Array.from(mixMap, ([position, count]) => ({position, count})),
    };
}

const money = (v: number) => formatCompactCurrency(v, COVERAGE_CURRENCY);

function buildSteps(intention: PlanIntention, s: PlanScope): string[] {
    const countriesTxt = s.countries.length ? ` across ${s.countries.join(', ')}` : '';
    const steps: string[] = [];

    switch (intention) {
        case 'consolidate':
            steps.push(`Define one agreement covering ${s.count} supplier${s.count === 1 ? '' : 's'} — ${money(s.spend)} cumulative spend${countriesTxt} and ${formatNumber(s.entities)} legal entities.`);
            if (s.uncontracted > 0)
                steps.push(`Bring the ${s.uncontracted} uncontracted supplier${s.uncontracted === 1 ? '' : 's'} (${money(s.uncontractedSpend)}) under the new master agreement.`);
            if (s.aboveCount > 0)
                steps.push(`Fold in the ${s.aboveCount} supplier${s.aboveCount === 1 ? '' : 's'} currently ${money(s.overSpend)} above contracted value and renegotiate that excess.`);
            steps.push(`Standardise terms and assign a single owner across ${formatNumber(s.transactions)} transactions.`);
            break;
        case 'renegotiate':
            if (s.aboveCount > 0)
                steps.push(`Prioritise the ${s.aboveCount} above-contract supplier${s.aboveCount === 1 ? '' : 's'}: address ${money(s.overSpend)} billed above the contracted value.`);
            if (s.requiredRecommended > 0)
                steps.push(`Open negotiations for the ${s.requiredRecommended} supplier${s.requiredRecommended === 1 ? '' : 's'} without a contract (${money(s.uncontractedSpend)}).`);
            steps.push(`Negotiate as one package — ${money(s.spend)} of combined leverage${countriesTxt}.`);
            break;
        case 'rationalise':
            steps.push(`Assess overlap across ${s.count} supplier${s.count === 1 ? '' : 's'} (${money(s.spend)}, ${formatNumber(s.transactions)} transactions) and identify candidates to retire.`);
            if (s.belowCount > 0)
                steps.push(`Wind down or right-size the ${s.belowCount} under-utilised contract${s.belowCount === 1 ? '' : 's'} (cost avoidance ${money(s.avoidance)}).`);
            steps.push(`Concentrate retained volume on the strongest supplier${s.count === 1 ? '' : 's'} to improve terms.`);
            break;
        case 'reevaluate':
            if (s.belowCount > 0)
                steps.push(`Re-evaluate the ${s.belowCount} under-utilised contract${s.belowCount === 1 ? '' : 's'} before renewal — cost avoidance ${money(s.avoidance)}.`);
            steps.push(`Validate demand and required scope across ${s.count} supplier${s.count === 1 ? '' : 's'} (${money(s.spend)}).`);
            break;
    }

    if (s.earliestExpiration)
        steps.push(`Anchor timing on the earliest contract expiration: ${formatDate(s.earliestExpiration)}.`);
    const impact: string[] = [];
    if (s.saving > 0) impact.push(`${money(s.saving)} cost saving`);
    if (s.avoidance > 0) impact.push(`${money(s.avoidance)} cost avoidance`);
    if (impact.length) steps.push(`Targeted impact: ${impact.join(' and ')}.`);

    return steps;
}

export function buildExecutionPlan(
    _parent: ContractParent, children: ContractChild[], intention: PlanIntention): ExecutionPlan {
    const scope = buildScope(children);
    return {
        intention,
        intentionLabel: PLAN_INTENTIONS.find(i => i.value === intention)!.label,
        scope,
        steps: buildSteps(intention, scope),
    };
}

export {POSITION_LABEL};
